/**
 * GTM Theme Functions - Shopify Theme Integration
 * 
 * This file provides helper functions for Shopify themes to trigger custom events
 * that will be captured by the Customer Event Pixel for GTM tracking.
 * 
 * Usage:
 * 1. Include this file in your theme's global scope (e.g., theme.liquid)
 * 2. Call the appropriate function when specific user interactions occur
 * 3. The pixel will automatically capture and send data to GTM
 * 
 * Features:
 * - Theme-based event detection and triggering
 * - Custom event publishing using Shopify.analytics.publish()
 * - Integration with existing Customer Event Pixel
 * - Error handling and validation
 * - Debug mode for development
 * 
 * @version 1.0.0
 * @requires Shopify.analytics (Shopify platform)
 * @author Shopify Theme Integration
 */

// Configuration and state management
window.GTMThemeFunctions = window.GTMThemeFunctions || {
  debug: false,
  initialized: false,
  version: '1.0.0'
};

// Enable debug mode (set to true during development)
const GTM_DEBUG = window.GTMThemeFunctions.debug;

/**
 * Utility function to safely publish events to Shopify analytics
 * @param {string} eventName - Name of the custom event
 * @param {Object} eventData - Data to pass with the event
 */
function publishCustomEvent(eventName, eventData = {}) {
  try {
    if (typeof Shopify !== 'undefined' && Shopify.analytics && Shopify.analytics.publish) {
      Shopify.analytics.publish(eventName, eventData);
      
      if (GTM_DEBUG) {
        console.log(`📊 GTM Theme Event Published: ${eventName}`, eventData);
      }
    } else {
      console.warn('⚠️ Shopify.analytics.publish not available');
    }
  } catch (error) {
    console.error('❌ Error publishing custom event:', error);
  }
}

/**
 * Newsletter Signup Event
 * Trigger when user subscribes to newsletter
 * @param {Object} data - Newsletter signup data
 */
function trackNewsletterSignup(data = {}) {
  const eventData = {
    email_provided: !!data.email,
    source: data.source || 'unknown', // e.g., 'footer', 'popup', 'product_page'
    campaign_id: data.campaign_id || null,
    timestamp: new Date().toISOString()
  };
  
  publishCustomEvent('newsletter_signup', eventData);
}

/**
 * Product Quick View Event
 * Trigger when user opens product quick view/modal
 * @param {Object} productData - Product information
 */
function trackProductQuickView(productData = {}) {
  const eventData = {
    product_id: productData.id || productData.product_id,
    product_title: productData.title || productData.name,
    product_handle: productData.handle,
    variant_id: productData.variant_id,
    price: productData.price,
    currency: productData.currency || 'USD',
    source: 'quick_view'
  };
  
  publishCustomEvent('product_quick_view', eventData);
}

/**
 * Wishlist Add Event
 * Trigger when user adds product to wishlist
 * @param {Object} productData - Product information
 */
function trackWishlistAdd(productData = {}) {
  const eventData = {
    product_id: productData.id || productData.product_id,
    product_title: productData.title || productData.name,
    variant_id: productData.variant_id,
    price: productData.price,
    currency: productData.currency || 'USD',
    action: 'add_to_wishlist'
  };
  
  publishCustomEvent('wishlist_action', eventData);
}

/**
 * Wishlist Remove Event
 * Trigger when user removes product from wishlist
 * @param {Object} productData - Product information
 */
function trackWishlistRemove(productData = {}) {
  const eventData = {
    product_id: productData.id || productData.product_id,
    product_title: productData.title || productData.name,
    variant_id: productData.variant_id,
    price: productData.price,
    currency: productData.currency || 'USD',
    action: 'remove_from_wishlist'
  };
  
  publishCustomEvent('wishlist_action', eventData);
}

/**
 * Product Compare Add Event
 * Trigger when user adds product to comparison
 * @param {Object} productData - Product information
 */
function trackCompareAdd(productData = {}) {
  const eventData = {
    product_id: productData.id || productData.product_id,
    product_title: productData.title || productData.name,
    variant_id: productData.variant_id,
    price: productData.price,
    currency: productData.currency || 'USD',
    action: 'add_to_compare'
  };
  
  publishCustomEvent('product_compare', eventData);
}

/**
 * Social Share Event
 * Trigger when user shares product via social media
 * @param {Object} shareData - Share information
 */
function trackSocialShare(shareData = {}) {
  const eventData = {
    platform: shareData.platform || 'unknown', // e.g., 'facebook', 'twitter', 'pinterest'
    content_type: shareData.content_type || 'product', // e.g., 'product', 'collection', 'page'
    content_id: shareData.content_id,
    content_title: shareData.content_title,
    url: shareData.url || window.location.href
  };
  
  publishCustomEvent('social_share', eventData);
}

/**
 * Video Play Event
 * Trigger when user plays product or promotional video
 * @param {Object} videoData - Video information
 */
function trackVideoPlay(videoData = {}) {
  const eventData = {
    video_title: videoData.title || 'Untitled Video',
    video_duration: videoData.duration,
    video_type: videoData.type || 'product', // e.g., 'product', 'promotional', 'tutorial'
    product_id: videoData.product_id,
    position: videoData.position || 0
  };
  
  publishCustomEvent('video_interaction', eventData);
}

/**
 * Review Submitted Event
 * Trigger when user submits product review
 * @param {Object} reviewData - Review information
 */
function trackReviewSubmitted(reviewData = {}) {
  const eventData = {
    product_id: reviewData.product_id,
    rating: reviewData.rating,
    review_length: reviewData.review_text ? reviewData.review_text.length : 0,
    has_photos: !!(reviewData.photos && reviewData.photos.length > 0),
    verified_purchase: reviewData.verified_purchase || false
  };
  
  publishCustomEvent('review_submitted', eventData);
}

/**
 * Size Guide View Event
 * Trigger when user opens size guide
 * @param {Object} guideData - Size guide information
 */
function trackSizeGuideView(guideData = {}) {
  const eventData = {
    product_id: guideData.product_id,
    product_type: guideData.product_type,
    guide_type: guideData.guide_type || 'size_chart'
  };
  
  publishCustomEvent('size_guide_view', eventData);
}

/**
 * Filter Applied Event
 * Trigger when user applies filters on collection pages
 * @param {Object} filterData - Filter information
 */
function trackFilterApplied(filterData = {}) {
  const eventData = {
    collection_id: filterData.collection_id,
    collection_title: filterData.collection_title,
    filter_type: filterData.filter_type, // e.g., 'price', 'color', 'size', 'brand'
    filter_value: filterData.filter_value,
    total_results: filterData.total_results,
    active_filters: filterData.active_filters || []
  };
  
  publishCustomEvent('filter_applied', eventData);
}

/**
 * Search No Results Event
 * Trigger when search returns no results
 * @param {Object} searchData - Search information
 */
function trackSearchNoResults(searchData = {}) {
  const eventData = {
    query: searchData.query,
    query_length: searchData.query ? searchData.query.length : 0,
    suggested_terms: searchData.suggestions || [],
    timestamp: new Date().toISOString()
  };
  
  publishCustomEvent('search_no_results', eventData);
}

/**
 * Promotion Banner Click Event
 * Trigger when user clicks promotional banners
 * @param {Object} bannerData - Banner information
 */
function trackPromotionClick(bannerData = {}) {
  const eventData = {
    banner_id: bannerData.id,
    banner_title: bannerData.title,
    banner_position: bannerData.position, // e.g., 'header', 'footer', 'sidebar'
    promotion_type: bannerData.type, // e.g., 'sale', 'new_arrival', 'free_shipping'
    destination_url: bannerData.url
  };
  
  publishCustomEvent('promotion_click', eventData);
}

/**
 * Cart Abandonment Intent Event
 * Trigger when user shows intent to leave with items in cart
 * @param {Object} cartData - Cart information
 */
function trackCartAbandonmentIntent(cartData = {}) {
  const eventData = {
    cart_value: cartData.total_price,
    item_count: cartData.item_count,
    currency: cartData.currency || 'USD',
    trigger: cartData.trigger || 'mouse_leave', // e.g., 'mouse_leave', 'tab_close'
    time_on_page: cartData.time_on_page
  };
  
  publishCustomEvent('cart_abandonment_intent', eventData);
}

/**
 * Live Chat Started Event
 * Trigger when user initiates live chat
 * @param {Object} chatData - Chat information
 */
function trackLiveChatStarted(chatData = {}) {
  const eventData = {
    page_type: chatData.page_type || 'unknown',
    product_id: chatData.product_id,
    chat_source: chatData.source || 'widget', // e.g., 'widget', 'button', 'popup'
    timestamp: new Date().toISOString()
  };
  
  publishCustomEvent('live_chat_started', chatData);
}

/**
 * Account Creation Started Event
 * Trigger when user begins account creation process
 * @param {Object} accountData - Account creation information
 */
function trackAccountCreationStarted(accountData = {}) {
  const eventData = {
    source: accountData.source || 'unknown', // e.g., 'checkout', 'header', 'product_page'
    has_marketing_consent: accountData.marketing_consent || false
  };
  
  publishCustomEvent('account_creation_started', eventData);
}

// Automatic event listeners for common interactions
function initializeAutoTracking() {
  // Track outbound link clicks
  document.addEventListener('click', function(event) {
    const link = event.target.closest('a');
    if (link && link.hostname !== window.location.hostname) {
      publishCustomEvent('outbound_link_click', {
        url: link.href,
        text: link.textContent.trim(),
        hostname: link.hostname
      });
    }
  });
  
  // Track scroll depth milestones
  let scrollMilestones = [25, 50, 75, 90];
  let trackedMilestones = [];
  
  window.addEventListener('scroll', function() {
    const scrollPercent = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
    
    scrollMilestones.forEach(milestone => {
      if (scrollPercent >= milestone && !trackedMilestones.includes(milestone)) {
        trackedMilestones.push(milestone);
        publishCustomEvent('scroll_depth', {
          milestone: milestone,
          page_url: window.location.href,
          page_title: document.title
        });
      }
    });
  });
  
  // Track form abandonment (basic implementation)
  const forms = document.querySelectorAll('form[data-track-abandonment]');
  forms.forEach(form => {
    let formStarted = false;
    let formCompleted = false;
    
    form.addEventListener('input', function() {
      if (!formStarted) {
        formStarted = true;
        publishCustomEvent('form_started', {
          form_id: form.id || form.className,
          form_type: form.dataset.formType || 'unknown'
        });
      }
    });
    
    form.addEventListener('submit', function() {
      formCompleted = true;
    });
    
    window.addEventListener('beforeunload', function() {
      if (formStarted && !formCompleted) {
        publishCustomEvent('form_abandoned', {
          form_id: form.id || form.className,
          form_type: form.dataset.formType || 'unknown'
        });
      }
    });
  });
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  if (!window.GTMThemeFunctions.initialized) {
    initializeAutoTracking();
    window.GTMThemeFunctions.initialized = true;
    
    if (GTM_DEBUG) {
      console.log('✅ GTM Theme Functions initialized successfully');
    }
  }
});

// Make functions globally available
window.trackNewsletterSignup = trackNewsletterSignup;
window.trackProductQuickView = trackProductQuickView;
window.trackWishlistAdd = trackWishlistAdd;
window.trackWishlistRemove = trackWishlistRemove;
window.trackCompareAdd = trackCompareAdd;
window.trackSocialShare = trackSocialShare;
window.trackVideoPlay = trackVideoPlay;
window.trackReviewSubmitted = trackReviewSubmitted;
window.trackSizeGuideView = trackSizeGuideView;
window.trackFilterApplied = trackFilterApplied;
window.trackSearchNoResults = trackSearchNoResults;
window.trackPromotionClick = trackPromotionClick;
window.trackCartAbandonmentIntent = trackCartAbandonmentIntent;
window.trackLiveChatStarted = trackLiveChatStarted;
window.trackAccountCreationStarted = trackAccountCreationStarted;

// Expose configuration for external access
window.GTMThemeFunctions.enableDebug = function() {
  window.GTMThemeFunctions.debug = true;
  console.log('🔍 GTM Theme Functions debug mode enabled');
};

window.GTMThemeFunctions.disableDebug = function() {
  window.GTMThemeFunctions.debug = false;
  console.log('🔇 GTM Theme Functions debug mode disabled');
};

// Log successful initialization
if (GTM_DEBUG) {
  console.log('📋 GTM Theme Functions v1.0.0 loaded');
  console.log('🎯 Available functions:', Object.keys(window).filter(key => key.startsWith('track')));
}
